<template>
  <div>
    <div class="bg-card p-4 mb-3 d-flex flex-wrap">
      <div class="col-12 col-md-2 mb-3 m-md-0 mr-md-4">
        <img
          class="company-logo"
          :src="basePath + 'uploads/company_logos/' + company.avatar.filename"
        >
      </div>
      <div class="col-12 col-md-9 ml-2">
        <h1 class="company-title">
          {{ company.title }}
        </h1>
        <div><span style="font-size: 0.9rem">{{ $t('pages.my-account.company.cif', 'CIF') }}: </span><strong>{{ company.number }}</strong></div>
        <div><span style="font-size: 0.9rem">{{ $t('pages.my-account.company.phone', 'Phone number') }}: </span>{{ company.phone }}</div>
        <div class="company-descr">
          {{ company.description.substring(0, 155) }}...
        </div>
      </div>
    </div>
    <div class="bg-card">
      <h2>{{ $t('pages.my-account.company.form.title', 'Edit information') }}</h2>
      <form>
        <div class="form-row">
          <b-form-group
            id="company-avatar"
            class="col"
            :label="$t('pages.my-account.company.form.logo.label', 'Update the company logo')"
          >
            <div v-if="cavatar">
              <img
                v-if="url"
                :src="url"
                class="avatar-preview company-logo"
              >
              <div
                class="clear-avatar"
                @click="cavatar = null; url = null;"
              >
                {{ $t('generic.clear', 'Clear') }}
              </div>
            </div>
            <b-form-file
              v-model="cavatar"
              :lang="$i18n.locale()"
              :placeholder="$t('pages.my-account.company.form.logo.placeholder', 'Select an image or drop it here')"
              :drop-placeholder="$t('pages.my-account.company.form.logo.drop-placeholder', 'Drop it here...')"
              :browse-text="$t('pages.my-account.company.form.logo.browse-text', 'Browse')"
              size="sm"
              @change="onFileChange"
            />
          </b-form-group>
        </div>
        <div class="form-row">
          <b-form-group
            id="company-title"
            class="col"
            :label="`${$t('pages.my-account.company.form.company-name.label', 'Company name')}*`"
            label-for="title"
            :invalid-feedback="$t('pages.my-account.company.form.company-name.validation', 'Invalid company name')"
            :state="titleState"
          >
            <b-form-input
              id="title"
              v-model="company.title"
              disabled
            />
            <b-tooltip target="company-title">
              {{ $t('pages.my-account.company.form.company-name.tooltip', 'To change company\'s title please contact administration') }}
            </b-tooltip>
          </b-form-group>
        </div>
        <div class="form-row">
          <b-form-group
            class="col-md-6"
            :label="$t('pages.my-account.company.form.country.label', 'Country')"
            label-for="country"
            :invalid-feedback="$t('pages.my-account.company.form.country.validation', 'Invalid country')"
            :state="phoneState"
          >
            <b-form-select
              id="country"
              v-model="company.country"
              :options="countryOptions"
            />
          </b-form-group>
          <b-form-group
            id="province"
            class="col-md-6"
            :label="$t('pages.my-account.company.form.province.label', 'Province')"
            label-for="province"
            :invalid-feedback="$t('pages.my-account.company.form.province.validation', 'Invalid province')"
            :state="numberState"
          >
            <b-form-select
              id="province"
              v-model="company.province"
              :options="provinceOptions"
              :disabled="company.country != 'España'"
            />
          </b-form-group>
        </div>
        <div class="form-row">
          <b-form-group
            class="col"
            :label="$t('pages.my-account.company.form.category.label', 'Category')"
            label-for="category"
          >
            <b-form-select
              id="category"
              v-model="company.category"
              :options="categoryOptions"
            />
          </b-form-group>
        </div>
        <div class="form-row">
          <b-form-group
            class="col-md-6"
            :label="$t('pages.my-account.company.form.phone.label', 'Phone number') + '*'"
            label-for="phone"
            :invalid-feedback="$t('pages.my-account.company.form.phone.validation', 'Invalid phone number')"
            :state="phoneState"
          >
            <b-form-input
              id="phone"
              v-model="company.phone"
            />
          </b-form-group>
          <b-form-group
            id="company-cif"
            class="col-md-6"
            :label="$t('pages.my-account.company.form.company-cif.label', 'Company CIF') + '*'"
            label-for="number"
            :invalid-feedback="$t('pages.my-account.company.form.company-cif.validation', 'Invalid company CIF')"
            :state="numberState"
          >
            <b-form-input
              id="number"
              v-model="company.number"
              disabled
            />
            <b-tooltip target="company-cif">
              {{ $t('pages.my-account.company.form.company-cif.tooltip', 'You can\'t change CIF!') }}
            </b-tooltip>
          </b-form-group>
        </div>
        <div class="form-row">
          <div class="form-group col">
            <label for="description">{{ $t('pages.my-account.company.form.description.label', 'Company description') }}</label>
            <textarea
              id="description"
              v-model="company.description"
              class="form-control"
              cols="4"
              rows="6"
              :placeholder="$t('pages.my-account.company.form.description.placeholder', 'Describe your company for the candidates here')"
            />
          </div>
        </div>
        <button
          class="btn btn-primary float-right"
          :disabled="!readyToSend"
          @click.prevent="updateCompany()"
        >
          {{ $t('generic.save', 'Save') }}
        </button>
      </form>
    </div>
  </div>
</template>

<script>
import {
  countries,
  provinces,
  categories,
} from "@/assets/data";

export default {
  data() {
    return {
      company: {
        title: "",
        country: "",
        province: "",
        category: "",
        phone: "",
        number: "",
        description: "",
        avatar: {
          filename: ""
        }
      },
      cavatar: null,
      url: null,
    }
  },

  computed: {
    countryOptions: () => countries(),

    provinceOptions() {
      if (this.company.country !== "España") {
        return [{
          value: this.company.country,
          text: this.$t(`data.countries.${this.company.country}`, this.company.country)
        }];
      }
      return provinces();
    },

    categoryOptions: () => categories(),

    titleState() {
      return this.company && this.company.title && this.company.title.length >= 4;
    },

    phoneState() {
      return this.company && this.company.phone && this.company.phone.length >= 4;
    },

    numberState() {
      return this.company && this.company.number && this.company.number.length >= 4;
    },

    readyToSend() {
      return this.titleState && this.phoneState && this.numberState;
    },

    basePath() {
      return process.env.VUE_APP_API_URL + process.env.VUE_APP_API_PUBLIC_PATH;
    }
  },

  watch: {
    company: {
      handler(val) {
        if (val.country !== "España") {
          this.company.province = val.country || "";
        } else {
          this.company.province = val.province;
        }
      },
      deep: true
    }
  },

  created() {
    this.company = this.$auth.user().company;
  },

  methods: {
    async updateCompany() {
      let formData = this.company;
      if (this.cavatar != null) {
        // Upload avatar
        formData.avatar_id = await this.uploadAvatar();
      }

      this.axios.put('/company', formData)
        .then( response => {
          this.company = response.data;
          this.cavatar = null;
          this.$auth.user().company = this.company;

          this.$toastNotification('success', '', this.$t('pages.my-account.company.update-success', 'Company updated successfully!'));
        })
        .catch();
    },

    onFileChange(e) {
      const file = e.target.files[0];
      this.url = URL.createObjectURL(file);
    },

    uploadAvatar() {
      const fd = new FormData();
      fd.append('category', 'company_logos');
      fd.append('file', this.cavatar);

       // Upload a file
      return this.axios
        .post('/files/upload', fd, {
          headers: {
            'Content-type': 'multipart/form-data'
          },
        })
        .then(response => {
          return response.data.id;
        });
    }
  }
}
</script>

<style lang="scss">
  .company-logo {
    width: 120px;
    height: 120px;
    border-radius: 5px;
    border: 1px solid #efefef;
    box-shadow: 2px 3px 15px 10px rgba(0, 0, 0, 0.1), -1px 0 2px 0px rgba(0, 0, 0, 0.05);
  }

  h1.company-title {
    color: #009090;
    font-size: 1.2rem !important;
    text-transform: uppercase;
    margin-bottom: 0;
  }

  .company-descr {
    font-size: 0.9rem;
    color: #666;
  }

  .avatar-preview {
    // TODO: remove or use this
    // margin-bottom: 0.4rem;
  }

  .clear-avatar {
    font-size: 0.9rem;
    color: #dc3545;
    width: 120px;
    text-align: center;
    margin-bottom: 5px;
    cursor: pointer;

    &:hover {
      font-weight: 600;
    }
  }
</style>

